import { z } from "zod";
import {
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
} from "../util/index.ts";

export const UserInterfaceConfigurationScope = {
  COMPONENT: "component",
  VIEW: "view",
  MULTI_VIEW: "multi-view",
} as const;
export type UserInterfaceConfigurationScopeType =
  (typeof UserInterfaceConfigurationScope)[keyof typeof UserInterfaceConfigurationScope];

export const UserInterfaceConfigurationStatus = {
  DRAFT: "draft",
  ACTIVE: "active",
} as const;
export type UserInterfaceConfigurationStatusType =
  (typeof UserInterfaceConfigurationStatus)[keyof typeof UserInterfaceConfigurationStatus];

export const UserInterfaceConfigurationVisibility = {
  PERSONAL: "personal",
  COMPANY: "company",
} as const;
export type UserInterfaceConfigurationVisibilityType =
  (typeof UserInterfaceConfigurationVisibility)[keyof typeof UserInterfaceConfigurationVisibility];

export const UserInterfaceConfigurationSchema = z
  .object({
    company_id: z.string().uuid(),
    owner_id: z.string().uuid(),
    name: z.string().min(1).max(256),
    description: z.string().min(0).max(1024).optional().default(""),
    scope: z
      .nativeEnum(UserInterfaceConfigurationScope)
      .default(UserInterfaceConfigurationScope.COMPONENT),
    type: z.string().min(1).max(240),
    key: z.array(z.string().min(1).max(120)),
    configuration: z.unknown(),
    status: z
      .nativeEnum(UserInterfaceConfigurationStatus)
      .default(UserInterfaceConfigurationStatus.DRAFT),
    visibility: z
      .nativeEnum(UserInterfaceConfigurationVisibility)
      .default(UserInterfaceConfigurationVisibility.COMPANY),
    is_default: z.boolean().default(false),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe("Represents a User Interface Configuration model in the Node API");
export type UserInterfaceConfigurationType = z.infer<
  typeof UserInterfaceConfigurationSchema
>;

/**
 * Helper function to create a UserInterfaceConfigurationSchema with a specific configuration schema
 * @param configurationSchema schema for the configuration. must be an object schema.
 * @returns UserInterfaceConfigurationSchema with the 'configuration' property set to the specified schema
 */
export const getUserInterfaceConfigurationSchema = <T extends z.ZodRawShape>(
  configurationSchema: z.ZodObject<T>
) => {
  return UserInterfaceConfigurationSchema.merge(
    z.object({
      configuration: configurationSchema,
    })
  );
};

export const GetListUserInterfaceConfigurationFiltersSchema =
  UserInterfaceConfigurationSchema.pick({
    id: true,
    company_id: true,
    owner_id: true,
    name: true,
    description: true,
    scope: true,
    type: true,
    key: true,
    status: true,
    visibility: true,
    is_default: true,
    created_at: true,
    updated_at: true,
    created_by: true,
    updated_by: true,
  }).partial();
export type GetListUserInterfaceConfigurationFiltersType = z.infer<
  typeof GetListUserInterfaceConfigurationFiltersSchema
>;

export const CreateUserInterfaceConfigurationSchema =
  UserInterfaceConfigurationSchema.pick({
    name: true,
    description: true,
    scope: true,
    type: true,
    key: true,
    configuration: true,
    status: true,
    visibility: true,
    is_default: true,
  }).merge(
    UserInterfaceConfigurationSchema.pick({
      owner_id: true,
    }).partial()
  );
export type CreateUserInterfaceConfigurationType = z.infer<
  typeof CreateUserInterfaceConfigurationSchema
>;

export const UpdateUserInterfaceConfigurationSchema =
  UserInterfaceConfigurationSchema.pick({
    name: true,
    description: true,
    scope: true,
    key: true,
    configuration: true,
    status: true,
    visibility: true,
    owner_id: true,
    is_default: true,
  }).partial();
export type UpdateUserInterfaceConfigurationType = z.infer<
  typeof UpdateUserInterfaceConfigurationSchema
>;
